import { useEffect, useRef, useState } from 'react';
import { lireEvenements } from '@/shared/utils/evenementsServeur';

/**
 * S'abonne au flux temps réel des actifs (`GET /api/monitoring/flux`).
 *
 * Retour de David du 28/09 : un écran qui se relit toutes les 30 s montre un ordre
 * trente secondes après le clic. Le serveur dit désormais QUAND un actif change ; l'écran
 * relit alors ce qu'il affiche, par ses routes habituelles.
 *
 * Le flux ne remplace pas le rafraîchissement périodique, il le ralentit. L'appelant
 * garde son intervalle et le règle sur `etat` :
 *
 *   temps_reel     flux ouvert : relecture sur événement, filet lent en plus
 *   connexion      ouverture en cours
 *   interrompu     flux tombé, on retente ; l'écran relit toutes les 10 s en attendant
 *   indisponible   refusé (droits, module non souscrit) : pas de nouvel essai
 *
 * Un flux qui se tait plus de `SILENCE_MAX_MS` est traité comme mort, même si la
 * connexion TCP tient encore : sans battement, rien ne prouve que les événements passent.
 *
 * @param {object}   options
 * @param {boolean}  options.actif     false = ne pas s'abonner (auto-refresh coupé)
 * @param {function} options.onActifs  appelé avec chaque lot `{ actifs, causes }`
 */
const BACKOFF_MS = [1000, 3000, 5000, 10000, 30000];
const SILENCE_MAX_MS = 45000;
// Le flux s'ouvre APRÈS le chargement de la page, pas pendant. Deux raisons : la liste
// initiale n'a pas à partager la bande passante avec une connexion qui ne rend rien
// d'utile avant elle ; et une connexion ouverte en permanence empêche d'atteindre l'état
// « réseau au repos » qu'attendent les recettes Playwright (`networkidle`), qui
// expireraient toutes sur Monitoring et Pilotage. Voir deploy/CLAUDE.md.
const DELAI_OUVERTURE_MS = 2000;

export default function useFluxActifs({ actif = true, onActifs }) {
  const [etat, setEtat] = useState(actif ? 'connexion' : 'interrompu');
  const rappel = useRef(onActifs);
  rappel.current = onActifs;

  useEffect(() => {
    if (!actif) { setEtat('interrompu'); return undefined; }
    let ferme = false;
    let essai = 0;
    let controleur = null;
    let minuterie = null;
    let veilleur = null;

    const relancer = () => {
      if (ferme) return;
      setEtat('interrompu');
      const delai = BACKOFF_MS[Math.min(essai, BACKOFF_MS.length - 1)];
      essai += 1;
      minuterie = setTimeout(ouvrir, delai);
    };

    const ouvrir = async () => {
      if (ferme) return;
      controleur = new AbortController();
      setEtat(e => (e === 'temps_reel' ? e : 'connexion'));
      let dernierSigne = Date.now();
      clearInterval(veilleur);
      veilleur = setInterval(() => {
        if (Date.now() - dernierSigne > SILENCE_MAX_MS) controleur.abort();
      }, 5000);
      try {
        const jeton = localStorage.getItem('flexcore_token');
        const reponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/monitoring/flux`, {
          headers: { Authorization: `Bearer ${jeton}`, Accept: 'text/event-stream' },
          signal: controleur.signal,
          cache: 'no-store',
        });
        if (reponse.status === 401 || reponse.status === 403) {
          // Un refus n'est pas une panne réseau : retenter en boucle ne ferait que
          // remplir les journaux. L'écran reste sur son rafraîchissement périodique.
          clearInterval(veilleur);
          setEtat('indisponible');
          return;
        }
        if (!reponse.ok || !reponse.body) throw new Error(`flux ${reponse.status}`);
        const lecteur = reponse.body.getReader();
        const decodeur = new TextDecoder();
        let tampon = '';
        for (;;) {
          const { value, done } = await lecteur.read();
          if (done) break;
          dernierSigne = Date.now();
          tampon += decodeur.decode(value, { stream: true });
          const { evenements, reste } = lireEvenements(tampon);
          tampon = reste;
          for (const { nom, donnees } of evenements) {
            if (nom === 'ouvert') { essai = 0; setEtat('temps_reel'); }
            if (nom === 'actifs') rappel.current?.(donnees);
          }
        }
      } catch {
        /* coupure, fermeture de l'onglet ou silence trop long : on retente plus bas */
      }
      clearInterval(veilleur);
      // Le serveur ferme volontairement au bout d'un quart d'heure pour rejouer les
      // gardes : la reconnexion est immédiate si le flux était sain.
      relancer();
    };

    minuterie = setTimeout(ouvrir, DELAI_OUVERTURE_MS);
    return () => {
      ferme = true;
      clearTimeout(minuterie);
      clearInterval(veilleur);
      controleur?.abort();
    };
  }, [actif]);

  return { etat };
}

/** Rythme du filet de sécurité, selon l'état du flux. */
export const RYTHME_REPLI_MS = 10000;     // flux tombé : ce que David a demandé
export const RYTHME_FILET_MS = 60000;     // flux ouvert : au cas où un événement se perde

export function rythmeSelonFlux(etat) {
  return etat === 'temps_reel' ? RYTHME_FILET_MS : RYTHME_REPLI_MS;
}
